import { evaluateFormula } from "@yjsgames/formula-evaluator";
import type {
  ProjectileBehavior,
  ProjectileElement,
  ProjectileImpact,
  ProjectileSpec,
} from "../models/ProjectileSpec.js";

export type ModuleProcKind = "normal" | "critical";

/**
 * 모듈 **등급별 효과 데이터** (dotknight skill effect{Grade}Json 방식의 phaser-brick 포팅).
 * 같은 모듈이 등급(common/rare/epic/legendary)마다 다른 효과를 갖도록 `modules.tsv`의
 * effect{Common,Rare,Epic,Legendary} 컬럼에 데이터로 명시한다 → 코드 하드코딩 0, 등급별 세부 조정 가능.
 *
 * 컬럼 값은 따옴표 없는 **mini-DSL** `key=expr|key2=expr2` (TSV 셀에 JSON 따옴표 충돌 회피).
 * 각 expr는 `{level}` placeholder를 쓰는 산술식(@yjsgames/formula-evaluator, CSP-safe).
 * 필드별 연산은 고정(applyModuleEffect 참조): 곱(×)·합(+)·max·enum 설정.
 */
export interface ModuleEffectData {
  /** 발사체 수 배수 (spec.count × eval). double=2, triple=3. */
  countMul?: string;
  /** 피해 배율 곱 (damageMultiplier × eval). double=0.9, amplify=2. */
  damageMul?: string;
  /** 분열 추가탄 (splitCount += eval). split. */
  splitCount?: string;
  /** 관통 추가 (pierceCount += eval). pierce. */
  pierce?: string;
  /** 연쇄 추가 (chainCount += eval). chain. */
  chain?: string;
  /** 폭발 반경 추가 (explosionRadius += eval). explosion. */
  explosionRadius?: string;
  /** 벽 반사 추가 (bounceCount += eval). bounce. */
  bounce?: string;
  /** 연사 수 (burstCount = max(spec, eval)). burst. */
  burst?: string;
  /** 연사 지연 ms (burstDelayMs = eval). burst. */
  burstDelayMs?: string;
  /** 치명타 확률 추가 (critChance += eval). crit. */
  critChance?: string;
  /** 치명타 배수 (critMultiplier = max(spec, eval)). crit. */
  critMul?: string;
  /** 파이프 치명타 확률 (0~1). 모듈 effect가 발동한 뒤 추가 보너스를 판정한다. */
  pipeCritChance?: string;
  /** 파이프 치명타 성공 시 최소 발사체 수 (spec.count = max(spec.count, eval)). */
  pipeCritCount?: string;
  /** 파이프 치명타 성공 시 피해 배율 곱 (damageMultiplier × eval). */
  pipeCritDamageMul?: string;
  /** 골드 배수 곱 (goldMultiplier × eval). greed. */
  goldMul?: string;
  /** 속도 배수 곱 (speedMultiplier × eval). speed. */
  speedMul?: string;
  /** 이동 패턴 설정 (enum, expression 아님). bounce/homing. */
  behavior?: ProjectileBehavior;
  /** 충돌 종류 설정 (enum). explosion/pierce/chain. */
  impact?: ProjectileImpact;
  /** 속성 설정 (enum). fire/electric/ice (현재 비활성). */
  element?: ProjectileElement;
  /**
   * **발동 확률 게이트** (0~1 expr, {level} 허용). spec 필드가 아니라 이 effect 묶음 **전체**의 발동 확률 —
   * 발사 1회 단위로 rng() < p면 발동(아래 필드 모두 적용), 실패면 그 모듈 효과 전체 미적용(spec 통과).
   * 미기재 시 항상 발동(기존 동작). 등급별 질적 차등용: 낮은 등급=낮은 확률+패널티, 높은 등급=높은 확률+안정.
   */
  chance?: string;
  /**
   * **파이프라인 강화(downstream buff)** — 경로상 "바로 다음 1개" 모듈을 등급 +N단계 강화(legendary clamp).
   * spec 변형이 아니라 **다른 모듈에 영향**이므로 applyModuleEffect는 무시하고, CombatSystem.buildSpec이
   * 모듈 재생성 시 PipelineEnhancement(resolvePipelineEnhancements)로 소비한다. 촉매(catalyst) 모듈용.
   */
  enhanceNextRarity?: string;
  /**
   * **파이프라인 강화(downstream buff)** — 경로상 "바로 다음 1개" 모듈의 발동확률(chance) +X 가산(0~1 clamp).
   * enhanceNextRarity와 동일하게 spec 밖 메타 효과 — PipelineEnhancement가 소비한다. 정밀(precision) 모듈용.
   */
  enhanceNextChance?: string;
  /**
   * **이웃 효과 재발동(echo)** — 경로상 "바로 이전(i-1) 1개" 모듈의 효과를 이 확률(0~1 expr)로 한 번 더 발동.
   * spec 변형이 아니라 다른 모듈을 재apply하는 메타 효과 — applyModuleEffect는 무시하고 ProjectileSpecBuilder.build가
   * readEchoMeta로 읽어 소비한다. 재발동 시 이웃은 ALWAYS_PROC로 확정 발동(이웃 chance 게이트 이중판정 회피). 잔향(echoPrev) 모듈용.
   */
  echoPrevChance?: string;
  /**
   * **이웃 효과 재발동(echo)** — 경로상 "바로 다음(i+1) 1개" 모듈의 효과를 이 확률로 한 번 더 발동.
   * echoPrevChance와 동일한 메타 효과(방향만 반대). 전조(echoNext) 모듈용.
   */
  echoNextChance?: string;
  /** 처형 체력 비율 임계 (0~1, spec 필드). 충돌 벽돌 체력이 maxHp의 이 비율 이하면 처형 후보. execute 모듈. */
  executeThreshold?: string;
  /** 처형 발동 확률 (0~1). 임계 이하 벽돌을 이 확률로 즉사(BattlefieldRunner.rollExecute). execute 모듈. */
  executeChance?: string;
  /** 충격파 반경(px). 충돌 지점 주변 벽돌에 별도 파동 피해와 링 연출을 낸다. */
  shockwaveRadius?: string;
  /** 충격파 피해 비율. 직격 피해량에 곱해 주변 피해량을 만든다. */
  shockwaveDamageRatio?: string;
  /** 여진 피해 비율. 충돌 후 같은 벽돌에 지연 2차 피해를 준다. */
  aftershockDamageRatio?: string;
  /** 여진 지연 시간(ms). */
  aftershockDelayMs?: string;
  /** 표식 부여 확률(0~1). 성공 시 벽돌에 약점 상태를 표시하고 받는 피해 배수를 적용한다. */
  markChance?: string;
  /** 표식 대상이 받는 피해 배수. */
  markDamageMul?: string;
  /** 표식 유지 타격 수. */
  markHits?: string;
  /** 발사 간격 배수 곱 (fireIntervalMultiplier × eval). 1 미만이면 속사(쿨타임 감소). rapid 모듈. */
  fireIntervalMul?: string;
}

/** expression(평가 대상) 필드. */
const EXPR_KEYS: readonly (keyof ModuleEffectData)[] = [
  "countMul",
  "damageMul",
  "splitCount",
  "pierce",
  "chain",
  "explosionRadius",
  "bounce",
  "burst",
  "burstDelayMs",
  "critChance",
  "critMul",
  "goldMul",
  "speedMul",
  "executeThreshold",
  "executeChance",
  "shockwaveRadius",
  "shockwaveDamageRatio",
  "aftershockDamageRatio",
  "aftershockDelayMs",
  "markChance",
  "markDamageMul",
  "markHits",
  "fireIntervalMul",
];
/** 파이프 치명타 전용 expression 필드 — applyModuleEffect가 아니라 applyPipeCriticalEffect가 소비한다. */
const PIPE_CRIT_KEYS: readonly (keyof ModuleEffectData)[] = [
  "pipeCritChance",
  "pipeCritCount",
  "pipeCritDamageMul",
];
/** 정보 표시용 메타 expression 필드 — spec 직접 변경은 아니지만 숙련도/모듈 설명에 노출한다. */
const DISPLAY_META_KEYS: readonly (keyof ModuleEffectData)[] = [
  "enhanceNextRarity",
  "enhanceNextChance",
  "echoPrevChance",
  "echoNextChance",
];
/** enum(설정) 필드 — 평가 없이 그대로 spec에 대입. */
const ENUM_KEYS: readonly (keyof ModuleEffectData)[] = ["behavior", "impact", "element"];
/** 게이트 필드 — spec에 적용되지 않고 effect 묶음 전체 발동 여부를 결정(확률 평가). */
const GATE_KEYS: readonly (keyof ModuleEffectData)[] = ["chance"];
/**
 * 메타 필드 — spec에 적용되지 않고 경로상 이웃 모듈에 영향(applyModuleEffect는 무시).
 * enhanceNext*=다음 모듈 강화(PipelineEnhancement 소비), echo*Chance=이웃 효과 재발동(ProjectileSpecBuilder.build 소비).
 */
const META_KEYS: readonly (keyof ModuleEffectData)[] = [
  "enhanceNextRarity",
  "enhanceNextChance",
  "echoPrevChance",
  "echoNextChance",
];
const VALID_KEYS = new Set<string>([
  ...EXPR_KEYS,
  ...PIPE_CRIT_KEYS,
  ...ENUM_KEYS,
  ...GATE_KEYS,
  ...META_KEYS,
]);

/** 확률 게이트 디폴트 rng — 항상 0 반환 = 어떤 양수 확률도 발동(표시/QA/회귀의 결정론 "항상 발동" 가정). */
export const ALWAYS_PROC: () => number = () => 0;
/** 파이프 치명타 디폴트 rng — 표시/요약 경로는 확률 보너스를 터뜨리지 않고 기본 효과만 보여준다. */
export const NEVER_PIPE_CRIT: () => number = () => 1;

/**
 * effect의 발동 확률 p (0~1)를 평가해 반환. chance 미기재면 undefined(= 게이트 없음 = 항상 발동).
 * 게이트 판정(applyModuleEffect)과 UI 표시(ModuleInfoPopup)가 공유하는 단일 출처.
 */
export function moduleEffectChance(
  effect: ModuleEffectData | undefined,
  level: number,
): number | undefined {
  if (!effect?.chance) return undefined;
  const p = evaluateFormula(effect.chance, { level });
  return Math.max(0, Math.min(1, p));
}

export function modulePipeCritChance(
  effect: ModuleEffectData | undefined,
  level: number,
): number | undefined {
  if (!effect?.pipeCritChance) return undefined;
  const p = evaluateFormula(effect.pipeCritChance, { level });
  return Math.max(0, Math.min(1, p));
}

/** 스탯 표기 그룹 — 필드별 연산(applyModuleEffect)에 맞춘 값 포맷. */
type StatGroup = "mul" | "add" | "pct" | "ms" | "count";
const STAT_GROUP: Partial<Record<keyof ModuleEffectData, StatGroup>> = {
  countMul: "mul",
  damageMul: "mul",
  goldMul: "mul",
  speedMul: "mul",
  critMul: "mul",
  pipeCritDamageMul: "mul",
  splitCount: "add",
  pierce: "add",
  chain: "add",
  explosionRadius: "add",
  bounce: "add",
  critChance: "pct",
  pipeCritChance: "pct",
  burstDelayMs: "ms",
  burst: "count",
  pipeCritCount: "count",
  enhanceNextRarity: "add",
  enhanceNextChance: "pct",
  echoPrevChance: "pct",
  echoNextChance: "pct",
  executeThreshold: "pct",
  executeChance: "pct",
  shockwaveRadius: "add",
  shockwaveDamageRatio: "pct",
  aftershockDamageRatio: "pct",
  aftershockDelayMs: "ms",
  markChance: "pct",
  markDamageMul: "mul",
  markHits: "count",
  fireIntervalMul: "mul",
};

/** 숫자 → 2자리 반올림 문자열 (정수는 정수, trailing 0 제거). */
function fmtStatNum(v: number): string {
  return String(Math.round(v * 100) / 100);
}

/** 그룹별 값 표기 — mul:×, add:+, pct:+N%, ms:Nms, count:N. applyModuleEffect 연산과 1:1. */
function formatStatValue(group: StatGroup, v: number): string {
  switch (group) {
    case "mul":
      return `×${fmtStatNum(v)}`;
    case "add":
      return `+${fmtStatNum(v)}`;
    case "pct":
      return `+${Math.round(v * 100)}%`;
    case "ms":
      return `${Math.round(v)}ms`;
    case "count":
      return fmtStatNum(v);
  }
}

/** 정보 팝업용 구체 스탯 1행 — 라벨 키(moduleStat.<key>) + 포맷된 값(현재 등급·레벨 평가). */
export interface ModuleEffectStat {
  readonly key: string;
  readonly value: string;
}

/**
 * 등급별 effect를 현재 레벨로 평가해 사람이 읽는 스탯 목록으로 변환한다 (순수, EXPR_KEYS 순서 유지).
 * chance 게이트(별도 chancePercent 표시)와 enum 필드(behavior/impact/element)는 수치가 아니라 제외.
 * 라벨은 i18n 키만 반환(moduleStat.<key>) — 호출자(buildModuleInfoData)가 t()로 현지화한다.
 */
export function evaluateModuleEffectStats(
  effect: ModuleEffectData,
  level: number,
): ModuleEffectStat[] {
  const out: ModuleEffectStat[] = [];
  for (const key of [...EXPR_KEYS, ...PIPE_CRIT_KEYS, ...DISPLAY_META_KEYS]) {
    const expr = effect[key];
    if (typeof expr !== "string" || !expr) continue;
    const group = STAT_GROUP[key];
    if (!group) continue;
    out.push({ key, value: formatStatValue(group, evaluateFormula(expr, { level })) });
  }
  return out;
}

/**
 * mini-DSL 문자열 → ModuleEffectData (순수). 빈/공백 문자열이면 빈 객체.
 * 형식: `key=expr|key2=expr2` (구분자 `|`, 키-값 `=`). 알 수 없는 키는 무시.
 */
export function parseModuleEffect(dsl: string | undefined): ModuleEffectData {
  const out: Record<string, string> = {};
  if (dsl && dsl.trim()) {
    for (const pair of dsl.split("|")) {
      const eq = pair.indexOf("=");
      if (eq < 0) continue;
      const key = pair.slice(0, eq).trim();
      const val = pair.slice(eq + 1).trim();
      if (VALID_KEYS.has(key) && val) out[key] = val;
    }
  }
  return out as ModuleEffectData;
}

export interface PipeCriticalApplyResult {
  readonly spec: ProjectileSpec;
  readonly critical: boolean;
}

export function applyPipeCriticalEffect(
  spec: ProjectileSpec,
  effect: ModuleEffectData,
  level: number,
  rng: () => number = NEVER_PIPE_CRIT,
): PipeCriticalApplyResult {
  const p = modulePipeCritChance(effect, level);
  if (p === undefined || rng() >= p) return { spec: { ...spec }, critical: false };
  const ev = (e: string): number => evaluateFormula(e, { level });
  const s: ProjectileSpec = { ...spec };
  if (effect.pipeCritCount) s.count = Math.max(s.count, ev(effect.pipeCritCount));
  if (effect.pipeCritDamageMul) s.damageMultiplier *= ev(effect.pipeCritDamageMul);
  return { spec: s, critical: true };
}

/**
 * 등급별 effect를 spec에 적용한다 (순수, 원본 mutate 금지). expression은 `{level}`만 치환해 평가.
 * 필드별 연산: countMul/damageMul/goldMul/speedMul=곱 · splitCount/pierce/chain/explosionRadius/bounce/critChance=합
 * · burst/critMul=max · behavior/impact/element=설정. 공통 레벨 피해 보너스는 호출자(DefinitionModule)가 별도 적용.
 */
export function applyModuleEffect(
  spec: ProjectileSpec,
  effect: ModuleEffectData,
  level: number,
  rng: () => number = ALWAYS_PROC,
): ProjectileSpec {
  // 발동 확률 게이트 — chance가 있고 rng()가 p 이상이면 발동 실패 → 이 effect 전체 미적용(spec 통과).
  const p = moduleEffectChance(effect, level);
  if (p !== undefined && rng() >= p) return { ...spec };
  const ev = (e: string): number => evaluateFormula(e, { level });
  const s: ProjectileSpec = { ...spec };
  if (effect.countMul) s.count *= ev(effect.countMul);
  if (effect.damageMul) s.damageMultiplier *= ev(effect.damageMul);
  if (effect.splitCount) s.splitCount += ev(effect.splitCount);
  if (effect.pierce) s.pierceCount += ev(effect.pierce);
  if (effect.chain) s.chainCount += ev(effect.chain);
  if (effect.explosionRadius) s.explosionRadius += ev(effect.explosionRadius);
  if (effect.bounce) s.bounceCount += ev(effect.bounce);
  if (effect.burst) s.burstCount = Math.max(s.burstCount, ev(effect.burst));
  if (effect.burstDelayMs) s.burstDelayMs = ev(effect.burstDelayMs);
  if (effect.critChance) s.critChance += ev(effect.critChance);
  if (effect.critMul) s.critMultiplier = Math.max(s.critMultiplier, ev(effect.critMul));
  if (effect.goldMul) s.goldMultiplier *= ev(effect.goldMul);
  if (effect.speedMul) s.speedMultiplier *= ev(effect.speedMul);
  if (effect.executeThreshold)
    s.executeThreshold = Math.max(s.executeThreshold, ev(effect.executeThreshold));
  if (effect.executeChance) s.executeChance = Math.max(s.executeChance, ev(effect.executeChance));
  if (effect.shockwaveRadius) s.shockwaveRadius += ev(effect.shockwaveRadius);
  if (effect.shockwaveDamageRatio)
    s.shockwaveDamageRatio = Math.max(s.shockwaveDamageRatio, ev(effect.shockwaveDamageRatio));
  if (effect.aftershockDamageRatio)
    s.aftershockDamageRatio = Math.max(s.aftershockDamageRatio, ev(effect.aftershockDamageRatio));
  if (effect.aftershockDelayMs) s.aftershockDelayMs = Math.max(s.aftershockDelayMs, ev(effect.aftershockDelayMs));
  if (effect.markChance) s.markChance = Math.max(s.markChance, ev(effect.markChance));
  if (effect.markDamageMul)
    s.markDamageMultiplier = Math.max(s.markDamageMultiplier, ev(effect.markDamageMul));
  if (effect.markHits) s.markHits = Math.max(s.markHits, ev(effect.markHits));
  if (effect.fireIntervalMul) s.fireIntervalMultiplier *= ev(effect.fireIntervalMul);
  if (effect.behavior) s.behavior = effect.behavior;
  if (effect.impact) s.impact = effect.impact;
  if (effect.element) s.element = effect.element;
  return s;
}
